import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, FormControl, FormLabel, RadioGroup, FormControlLabel,
  Radio, Alert, CircularProgress, Box, Divider,
} from '@mui/material';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';
import { ScanExamineConfirmDialogNext } from './ScanNext.jsx';
import GemaraSelectionEditor, {
  selectionToEntries,
  entriesToSelection,
  isGemaraEntriesValid,
} from './GemaraSelectionEditor.jsx';

const memberLabel = (member) => {
  if (!member) return '';
  const name = `${member.firstName || ''} ${member.lastName || ''}`.trim();
  const father = member.fatherName ? ` בן ${member.fatherName}` : '';
  return `${name}${father}`.trim() || `קוד ${member.personalCode}`;
};

const examLabel = (exam, gemaraSelection) => {
  if (exam) {
    const code = exam.code != null ? `[${exam.code}] ` : '';
    return `${code}${exam.name || ''}${exam.material ? ` — ${exam.material}` : ''}`.trim();
  }
  if (gemaraSelection?.length) {
    return gemaraSelection
      .map((s) => {
        const ranges = (s.ranges || [])
          .map((r) => `${r.from}–${r.to}`)
          .join(', ');
        return ranges ? `${s.tractate} (${ranges})` : s.tractate;
      })
      .filter(Boolean)
      .join(', ');
  }
  return 'ללא מבחן';
};

/**
 * דיאלוג אישור סימון נבחן אחרי סריקה.
 * תומך בבחירת הרשמה (כשיש כמה) ובבחירת/עריכת מסכתות וטווחים (open-gemara).
 *
 * props:
 *  - candidates: מערך של { registrationId, exam, examined, gemaraSelection, period }
 *  - member
 *  - onConfirm({ registrationId, gemaraSelection? | gemaraExamined? | examined? })
 *  - onClose
 */
/** In the new design the same props render ScanExamineConfirmDialogNext. */
const ScanExamineConfirmDialog = (props) => (useCkInside() ? <ScanExamineConfirmDialogNext {...props} /> : <ScanExamineConfirmDialogLegacy {...props} />);

const ScanExamineConfirmDialogLegacy = ({
  open,
  onClose,
  member,
  candidates = [],
  onConfirm,
  saving = false,
}) => {
  const [selectedRegId, setSelectedRegId] = useState('');
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState('');

  const selected = candidates.find((c) => c.registrationId === selectedRegId) || candidates[0];
  const gemara = selected?.gemaraSelection || [];
  const isGemara = gemara.length > 0;

  useEffect(() => {
    if (!open) return;
    setError('');
    const first = candidates[0];
    setSelectedRegId(first?.registrationId || '');
    setEntries(selectionToEntries(first?.gemaraSelection || []));
    // איפוס רק בפתיחה — לא מאפס באמצע עריכה כש־candidates משתנה ברפרנס
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!selected) {
      setEntries([]);
      return;
    }
    setEntries(selectionToEntries(selected.gemaraSelection || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegId]);

  const handleConfirm = () => {
    setError('');
    if (!selected?.registrationId) {
      setError('לא נמצאה הרשמה לסימון');
      return;
    }
    if (isGemara) {
      if (!isGemaraEntriesValid(entries)) {
        setError('יש למלא טווחי דפים תקינים לכל מסכת');
        return;
      }
      const gemaraSelection = entriesToSelection(entries);
      if (!gemaraSelection.some((g) => g.examined)) {
        setError('יש לסמן לפחות מסכת אחת כנבחן');
        return;
      }
      onConfirm?.({ registrationId: selected.registrationId, gemaraSelection });
      return;
    }
    onConfirm?.({ registrationId: selected.registrationId, examined: true });
  };

  const alreadyExamined = isGemara
    ? gemara.every((s) => s.examined)
    : !!selected?.examined;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>אישור סימון נבחן</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {memberLabel(member)}
        </Typography>
        {member?.personalCode != null && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            קוד אישי: {member.personalCode}
          </Typography>
        )}

        {candidates.length > 1 && (
          <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
            <FormLabel component="legend">בחירת הרשמה</FormLabel>
            <RadioGroup
              value={selectedRegId}
              onChange={(e) => setSelectedRegId(e.target.value)}
            >
              {candidates.map((c) => (
                <FormControlLabel
                  key={c.registrationId}
                  value={c.registrationId}
                  control={<Radio size="small" />}
                  label={examLabel(c.exam, c.gemaraSelection)}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}

        {candidates.length === 1 && selected && (
          <Box sx={{ mt: 1 }}>
            {!isGemara && (
              <Typography variant="body2" fontWeight={600}>
                {examLabel(selected.exam, selected.gemaraSelection)}
              </Typography>
            )}
            {selected.period?.name && (
              <Typography variant="body2" color="text.secondary">
                זמן: {selected.period.name}
              </Typography>
            )}
          </Box>
        )}

        {isGemara && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              ערוך את טווחי הדפים וסמן את המסכתות שהנרשם נבחן בהן:
            </Typography>
            <GemaraSelectionEditor
              value={entries}
              onChange={setEntries}
              showExamined
            />
          </>
        )}

        {alreadyExamined && (
          <Alert severity="info" sx={{ mt: 2 }}>
            כבר מסומן כנבחן — אישור יעדכן שוב את הסימון.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>ביטול</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleConfirm}
          disabled={saving || !selected?.registrationId || (isGemara && !isGemaraEntriesValid(entries))}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          סמן כנבחן
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScanExamineConfirmDialog;
